import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { buyerOrdersService } from '../../../services/buyerService';
import { format, startOfMonth } from 'date-fns';

const SpendingPatternsChart = ({ language, startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'Spending Patterns',
      spending: 'Total Spending (USD)',
      noData: 'No spending data available'
    },
    zh: {
      title: '支出模式',
      spending: '总支出（美元）',
      noData: '没有支出数据'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    loadSpendingData();
  }, [startDate, endDate]);

  const loadSpendingData = async () => {
    try {
      setLoading(true);
      const orders = await buyerOrdersService?.getAll();

      // Filter by date range
      const filtered = orders?.filter(order => {
        const orderDate = new Date(order?.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });

      // Group by month
      const grouped = filtered?.reduce((acc, order) => {
        const month = format(startOfMonth(new Date(order?.createdAt)), 'MMM yyyy');
        if (!acc?.[month]) {
          acc[month] = { month, spending: 0 };
        }
        acc[month].spending += (order?.quantityKg || 0) * (order?.bean?.priceUsdPerKg || 0);
        return acc;
      }, {});

      const chartData = Object.values(grouped);
      setData(chartData);
    } catch (error) {
      console.error('Failed to load spending:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="DollarSign" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
      </div>
      {data?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="BarChart" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
            <YAxis stroke="var(--color-muted-foreground)" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Bar dataKey="spending" fill="var(--color-primary)" name={t?.spending} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default SpendingPatternsChart;